import {
  Resource_AppointmentLocation,
  Resource_AppointmentRequestSetting,
  Resource_AppointmentTypeMsg,
  useGetAppointmentFormTemplateIdQuery,
  useGetPortalFacilityHierarchyQuery,
} from '@/generated/graphql';
import { MailOutlined, SelectOutlined } from '@ant-design/icons';
import { Error } from '@components/error';
import { AppointmentRequest } from '@components/facility-selection/facility-appointment-booking/appointment-request';
import { SlotRequest } from '@components/facility-selection/facility-appointment-booking/slot-request';
import { TimeFrameRequest } from '@components/facility-selection/facility-appointment-booking/time-frame-request';
import { FacilityAppointmentTypes } from '@components/facility-selection/facility-appointment-types';
import { FacilityContact } from '@components/facility-selection/facility-contact';
import { FacilityFaq } from '@components/facility-selection/facility-faq';
import { FacilityForm } from '@components/facility-selection/facility-form';
import { FacilityHeader } from '@components/facility-selection/facility-header';
import { FacilityList } from '@components/facility-selection/facility-list';
import { FacilityOpeningHours } from '@components/facility-selection/facility-opening-hours';
import { FacilitySelectionHelp } from '@components/facility-selection/facility-selection-help';
import styles from '@components/facility-selection/facility-selection.module.scss';
import { FacilityVisitingHours } from '@components/facility-selection/facility-visiting-hours';
import { AveliosSpinner } from '@components/loading/avelios-spinner';
import { PageWrapper } from '@components/page-wrapper';
import { Card, Col, Row, Space } from 'antd';
import Title from 'antd/lib/typography/Title';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FACILITY_FORM_KEY } from '../../constants/forms';
import { extractTranslation } from '../../helpers/formatters';
import { useCurrentUser } from '../../helpers/utils';

const FacilitySelection: NextPage = () => {
  const { formatMessage } = useIntl();

  const router = useRouter();
  const locale = router.locale;

  const { query } = router;
  const facilityId = query.facilityId as string;
  const facilityFormId = query[FACILITY_FORM_KEY] as string;

  const { userLoading } = useCurrentUser();

  const [selectedAppointmentType, setSelectedAppointmentTypeState] =
    useState<Nullable<Resource_AppointmentTypeMsg>>();
  const [selectedAppointmentLocation, setSelectedAppointmentLocationState] =
    useState<Nullable<Resource_AppointmentLocation>>();

  const setSelectedAppointmentType = (value: Nullable<Resource_AppointmentTypeMsg>) => {
    setSelectedAppointmentTypeState(() => value);

    const queryUpdate = router.query;

    if (value?.id) {
      queryUpdate['appointmentTypeId'] = value.id;

      const firstAppointmentLocation = value.appointmentLocations?.[0];
      if (
        firstAppointmentLocation &&
        firstAppointmentLocation !== Resource_AppointmentLocation.AppointmentLocationNone
      ) {
        queryUpdate['appointmentLocation'] = firstAppointmentLocation;
      } else {
        delete queryUpdate['appointmentLocation'];
      }
    } else {
      delete queryUpdate['appointmentTypeId'];
      delete queryUpdate['appointmentLocation'];
    }

    router.push({
      pathname: router.pathname,
      query: queryUpdate,
    });
  };

  const setSelectedAppointmentLocation = (value: Nullable<Resource_AppointmentLocation>) => {
    setSelectedAppointmentLocationState(() => value);
    router.push({
      pathname: router.pathname,
      query: { ...router.query, appointmentLocation: value },
    });
  };

  const { data: facilityData, error } = useGetPortalFacilityHierarchyQuery({
    variables: { rootPortalFacilityId: facilityId, depth: 0 },
  });
  const facility = facilityData?.getPortalFacilityHierarchy?.portalFacilities?.[0];
  const facilityContactNote = extractTranslation(facility?.contactNote, locale);

  const facilityFAQs = useMemo(
    () => facility?.frequentlyAskedQuestions?.filterPresent() ?? [],
    [facility?.frequentlyAskedQuestions],
  );

  const clinicId = facility?.clinicId;
  const officeHourId = facility?.officeHourId;

  const { data: formTemplateIdData, loading: formLoading } = useGetAppointmentFormTemplateIdQuery({
    skip: !clinicId || !officeHourId,
    variables: {
      clinicId: clinicId!,
      officeHourId: officeHourId!,
    },
  });

  const facilityFormTemplateVersioningGroupId =
    formTemplateIdData?.getAppointmentFormTemplateVersioningGroupId?.formTemplateVersioningGroupId;

  const appointmentTypeFormTemplateVersioningGroupId =
    selectedAppointmentType?.formTemplateVersioningGroupId;

  const hasFacilityForm = !!facilityFormTemplateVersioningGroupId && !facilityFormId?.length;

  const appointmentRequestSetting =
    selectedAppointmentType?.appointmentRequestConfig?.appointmentRequestSetting;

  let facilityAppointmentBooking;

  switch (appointmentRequestSetting) {
    case Resource_AppointmentRequestSetting.AppointmentRequestSettingSlotRequest:
      facilityAppointmentBooking = (
        <SlotRequest
          directBooking={true}
          appointmentType={selectedAppointmentType}
          appointmentLocation={selectedAppointmentLocation}
          clinicId={facility?.clinicId}
          facilityId={facility?.id}
          officeHourId={officeHourId}
        />
      );
      break;
    case Resource_AppointmentRequestSetting.AppointmentRequestSettingUntimedRequest:
      facilityAppointmentBooking = (
        <AppointmentRequest
          appointmentType={selectedAppointmentType}
          appointmentLocation={selectedAppointmentLocation}
          clinicId={facility?.clinicId}
          officeHourId={officeHourId}
        />
      );
      break;
    case Resource_AppointmentRequestSetting.AppointmentRequestSettingTimeframeRequest:
      facilityAppointmentBooking = (
        <TimeFrameRequest
          appointmentType={selectedAppointmentType}
          appointmentLocation={selectedAppointmentLocation}
          clinicId={facility?.clinicId}
          officeHourId={officeHourId}
        />
      );
      break;
    default:
      facilityAppointmentBooking = <Error />;
  }

  const { currentUser } = useCurrentUser();

  const verificationRequired =
    !clinicId ||
    (!currentUser?.verified &&
      currentUser?.accountVerificationRequiredForClinicIds?.includes(clinicId));

  return (
    <PageWrapper headerProps={undefined} error={!!error}>
      {facilityId && <FacilityHeader facility={facility} clinicId={clinicId} />}
      <Space style={{ width: '100%' }} direction={'vertical'} size={16}>
        {officeHourId && clinicId && hasFacilityForm && (
          <Row gutter={16}>
            <Col span={24}>
              <FacilityForm
                formObjectKey={FACILITY_FORM_KEY}
                officeHourId={officeHourId}
                clinicId={clinicId}
                facilityId={facilityId}
                formTemplateVersioningGroupId={facilityFormTemplateVersioningGroupId}
              />
            </Col>
          </Row>
        )}
        {!hasFacilityForm && officeHourId && (
          <div className={styles.wrapper}>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={14}>
                <FacilityAppointmentTypes
                  officeHourId={officeHourId}
                  clinicId={clinicId}
                  formId={facilityFormId}
                  selectedAppointmentType={selectedAppointmentType}
                  setSelectedAppointmentType={setSelectedAppointmentType}
                  selectedAppointmentLocation={selectedAppointmentLocation}
                  setSelectedAppointmentLocation={setSelectedAppointmentLocation}
                />
              </Col>
              <Col xs={24} lg={10}>
                <div style={{ height: '100%' }}>
                  <AveliosSpinner spinning={userLoading} style={{ height: '100%' }}>
                    {/* TODO: Add pre-slot selection form */}
                    <Card className={styles.nextAvailableAppointmentsWrapper}>
                      {selectedAppointmentType && selectedAppointmentLocation ? (
                        facilityAppointmentBooking
                      ) : (
                        <div>
                          <Title level={3}>
                            {formatMessage({
                              id: 'facility-selection.appointment-booking',
                            })}
                          </Title>
                          <div className={styles.selectIconWrapper}>
                            <div className={styles.selectIcon}>
                              <SelectOutlined />
                            </div>
                          </div>
                          {formatMessage({
                            id: 'facility-selection.select-type',
                          })}
                        </div>
                      )}
                    </Card>
                  </AveliosSpinner>
                </div>
              </Col>
            </Row>
            {verificationRequired && (
              <div>
                <div className={styles.verificationRequiredOverlay}>
                  <div className={styles.overlayWrapper}>
                    <div className={styles.iconWrapper}>
                      <div className={styles.emailIcon} onClick={() => router.push('/user')}>
                        <MailOutlined />
                      </div>
                    </div>
                    <Title>
                      {formatMessage({
                        id: 'facility-selection.list.facility-disabled',
                      })}
                    </Title>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <FacilityList />
        {facilityId && (
          <>
            <Row gutter={[16, 16]}>
              {facility?.contact && (
                <Col xs={24} lg={12}>
                  <FacilityContact
                    facilityName={facility?.name}
                    contact={facility?.contact}
                    facilityContactNote={facilityContactNote}
                  />
                </Col>
              )}
              <Col xs={24} lg={12}>
                <Space style={{ width: '100%' }} direction={'vertical'} size={16}>
                  {facility?.showOpeningHours && facility?.openingHours && (
                    <Row gutter={16}>
                      <Col span={24}>
                        <FacilityOpeningHours
                          openingHoursFreeText={facility?.openingHoursFreeText}
                          openingHours={facility?.openingHours}
                        />
                      </Col>
                    </Row>
                  )}
                  {facility?.showVisitingHours && facility?.visitingHours && (
                    <Row gutter={16}>
                      <Col span={24}>
                        <FacilityVisitingHours
                          visitingHoursFreeText={facility?.visitingHoursFreeText}
                          visitingHours={facility?.visitingHours}
                        />
                      </Col>
                    </Row>
                  )}
                </Space>
              </Col>
              {!!facilityFAQs.length && (
                <Col xs={24} lg={24}>
                  <Card>
                    <Title level={3}>
                      {formatMessage({ id: 'facility-selection.frequently-asked-questions' })}
                    </Title>
                    <FacilityFaq facilityFAQEntries={facilityFAQs} />
                  </Card>
                </Col>
              )}
            </Row>
          </>
        )}
        {!facilityId && <FacilitySelectionHelp />}
      </Space>
    </PageWrapper>
  );
};

export default FacilitySelection;
